"use client";

import { Core } from "@faire/design-tokens";
import { ILeanProductTile } from "@faire/web-api--source/indigofair/data/ILeanProductTile";
import { useLocalNum } from "@faire/web/common/localization";
import { Row } from "@faire/web/slate/Layout";
import { Swatch } from "@faire/web/slate/Swatch";
import { Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

export interface SwatchGroupProps {
  /** Array of swatch items to display */
  items?: Array<ILeanProductTile.IOptionsDetails.ISwatchAndOption>;
  /** Name for the radio group */
  name: string;
  totalSwatches: number;
}

export const SwatchGroup: React.FC<SwatchGroupProps> = ({
  items,
  name,
  totalSwatches,
}) => {
  const viewport = useViewport();
  const { localNum } = useLocalNum();

  const visibleCount = React.useMemo(() => {
    if (viewport.isXLargeAndAbove) {
      return 6;
    } else if (viewport.isDesktopAndAbove) {
      return 5;
    }
    return 4;
  }, [viewport.isXLargeAndAbove, viewport.isDesktopAndAbove]);

  if (!items) {
    return null;
  }

  const visibleItems = items.slice(0, visibleCount);
  const remainingCount = totalSwatches - visibleCount;

  return (
    <Row
      gap={8}
      align="center"
      className="pb-[8px]"
      data-testid="product-tile-swatches"
    >
      {visibleItems.map((item, index) => (
        <Swatch
          key={index}
          aria-label={`Swatch ${index + 1}`}
          image={item.swatch_image}
          swatchSize="small"
          name={name}
        />
      ))}
      {remainingCount > 0 && (
        <Typography variant="paragraphSansRegular" color={Core.text.subdued}>
          <StrictLocalMsg
            defaultMessage="+{remainingCountLocalizedNumber}"
            description={{
              text: "This shows a variable number of additional items that are not shown.",
              img: "https://cdn.faire.com/fastly/04013d4e6de3b57c5e00cb4933e117e4d205fafc265cab42bba1572f3a769645.png",
            }}
            values={{
              remainingCountLocalizedNumber: localNum(remainingCount),
            }}
          />
        </Typography>
      )}
    </Row>
  );
};
